// Vue WORKSPACES (LOT D-panel) partagée du plugin cli.
// Miroir TS du record C# CliWorkspace (WorkspaceManager.cs, camelCase wire).
import { Show, For, createSignal, onMount, type Component } from "solid-js";
import { useToolClient } from "@/shared/tool-client";
import { useConfirm } from "@/shared/confirm-dialog";
import { Collapsible } from "@/shared/collapsible";
import type { Workspace } from "./types";

// Déballe la réponse d'un outil workspace_* : soit le workspace nu, soit { workspace }.
export function unwrapWorkspace(r: unknown): Workspace | null {
  if (!r || typeof r !== "object") return null;
  const o = r as Record<string, unknown>;
  if (o.workspace && typeof o.workspace === "object") return o.workspace as Workspace;
  if (typeof o.hostPath === "string" || typeof o.id === "string") return o as unknown as Workspace;
  return null;
}

// Extrait le contexte hiérarchique CLAUDE.md/AGENTS.md si l'outil l'expose (champ
// `context` au top-level de la réponse, OU sur le workspace lui-même). Lecture seule.
export function extractContext(r: unknown): string | null {
  if (!r || typeof r !== "object") return null;
  const o = r as Record<string, unknown>;
  if (typeof o.context === "string" && o.context.length > 0) return o.context;
  const ws = o.workspace as Record<string, unknown> | undefined;
  if (ws && typeof ws.context === "string" && ws.context.length > 0) return ws.context;
  return null;
}

export interface WorkspacesViewProps {
  client: ReturnType<typeof useToolClient>;
  onTransfer: () => void;
  /** cwd courant du launcher CLI (signal partagé du panel). */
  currentCwd: () => string;
  /** Sélectionne un workspace → met à jour le cwd courant. */
  onSelectCwd: (cwd: string) => void;
}

export const WorkspacesView: Component<WorkspacesViewProps> = (props) => {
  const [rows, setRows] = createSignal<Workspace[]>([]);
  const [loading, setLoading] = createSignal(false);
  const [err, setErr] = createSignal("");
  const [busy, setBusy] = createSignal(""); // action en cours ("open"|"detect"|"add")
  const [pathInput, setPathInput] = createSignal("");
  // Contexte CLAUDE.md/AGENTS.md du workspace sélectionné (si exposé par l'outil).
  const [context, setContext] = createSignal<{ wsId: string; text: string } | null>(null);
  const confirm = useConfirm();

  async function refresh() {
    setLoading(true);
    setErr("");
    try {
      const r = (await props.client.call("workspace_list")) as { workspaces?: Workspace[] };
      setRows(r.workspaces || []);
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  // workspace_recent au montage (CONTRACTS §1) ; bascule sur workspace_list en cas d'absence.
  async function loadRecent() {
    setLoading(true);
    setErr("");
    try {
      const r = (await props.client.call("workspace_recent")) as { workspaces?: Workspace[] };
      const list = r.workspaces || [];
      if (list.length > 0) setRows(list);
      else await refresh();
    } catch {
      await refresh();
    } finally {
      setLoading(false);
    }
  }

  onMount(() => void loadRecent());

  // Sélection d'un workspace → cwd courant. Le contexte CLAUDE.md/AGENTS.md n'est
  // affiché que s'il a été retourné par workspace_open/detect (champ `context`).
  function selectWorkspace(w: Workspace, ctx?: string | null) {
    props.onSelectCwd(w.hostPath);
    setContext(ctx ? { wsId: w.id, text: ctx } : null);
  }

  async function openPath() {
    const p = pathInput().trim();
    if (!p) return;
    setBusy("open");
    setErr("");
    try {
      const r = await props.client.call("workspace_open", { path: p });
      const ws = unwrapWorkspace(r);
      if (ws) {
        upsert(ws);
        selectWorkspace(ws, extractContext(r));
        setPathInput("");
      }
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy("");
    }
  }

  async function detectPath() {
    const p = pathInput().trim();
    if (!p) return;
    setBusy("detect");
    setErr("");
    try {
      const r = await props.client.call("workspace_detect", { path: p });
      const ws = unwrapWorkspace(r);
      if (ws) {
        upsert(ws);
        selectWorkspace(ws, extractContext(r));
      }
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy("");
    }
  }

  // workspace_add_dir = DESTRUCTIF → confirmation + userConfirmedAt.
  async function addDir() {
    const p = pathInput().trim();
    if (!p) return;
    const ok = await confirm.confirm({
      title: "Ajouter un répertoire au workspace",
      body: `Ajouter « ${p} » au workspace courant ? Le répertoire sera accessible aux sessions CLI.`,
      level: "warn",
      confirmLabel: "Ajouter",
    });
    if (!ok) return;
    setBusy("add");
    setErr("");
    try {
      const ws = unwrapWorkspace(
        await props.client.call("workspace_add_dir", { path: p }, { userConfirmedAt: new Date().toISOString() }),
      );
      if (ws) {
        upsert(ws);
        setPathInput("");
      }
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy("");
    }
  }

  // Insère/remplace un workspace dans la liste (par id), le remontant en tête.
  function upsert(ws: Workspace) {
    setRows((prev) => [ws, ...prev.filter((w) => w.id !== ws.id)]);
  }

  return (
    <div class="cli-workspaces">
      <div class="cli-toolbar cli-ws-input-row">
        <input
          class="panel-filter-input cli-ws-path-input"
          value={pathInput()}
          placeholder="Chemin host d'un répertoire (ex: C:\\dev\\projet)…"
          autocomplete="off"
          spellcheck={false}
          onInput={(e) => setPathInput(e.currentTarget.value)}
          onKeyDown={(e) => e.key === "Enter" && pathInput().trim() && void openPath()}
        />
        <button class="btn btn--sm" onClick={() => void openPath()} disabled={!pathInput().trim() || busy() === "open"}>
          {busy() === "open" ? "Ouverture…" : "Ouvrir"}
        </button>
        <button
          class="btn btn--sm btn--ghost"
          onClick={() => void detectPath()}
          disabled={!pathInput().trim() || busy() === "detect"}
          title="Détecter la racine du workspace (remonte vers .git / CLAUDE.md…)"
        >
          {busy() === "detect" ? "Détection…" : "Détecter"}
        </button>
        <button
          class="btn btn--sm btn--warn"
          onClick={() => void addDir()}
          disabled={!pathInput().trim() || busy() === "add"}
          title="Ajouter ce répertoire au workspace courant (destructif)"
        >
          {busy() === "add" ? "Ajout…" : "Ajouter répertoire"}
        </button>
      </div>

      <div class="cli-toolbar">
        <button class="btn btn--sm btn--ghost" onClick={() => void refresh()} disabled={loading()}>
          {loading() ? "Chargement…" : "Actualiser"}
        </button>
        <button
          class="btn btn--sm btn--ghost"
          onClick={() => props.onTransfer()}
          title="Ouvrir l'explorateur de fichiers (transfert local ↔ host)"
        >
          Transférer…
        </button>
        <Show when={props.currentCwd()}>
          <span class="cli-ws-current" title="Répertoire de travail courant (cwd) utilisé par le launcher CLI">
            cwd : {props.currentCwd()}
          </span>
        </Show>
      </div>

      <Show when={err()}>
        <div class="panel-error">{err()}</div>
      </Show>

      <Show
        when={rows().length > 0}
        fallback={<div class="panel-empty">Aucun workspace. Saisissez un chemin et « Ouvrir ».</div>}
      >
        <div class="cli-ws-list">
          <For each={rows()}>
            {(w) => (
              <div class="cli-ws-row" data-active={props.currentCwd() === w.hostPath || undefined}>
                <span class="cli-ws-name">{w.name || w.hostPath}</span>
                <span class="cli-ws-path" title={w.hostPath}>
                  {w.hostPath}
                </span>
                <Show when={w.gitRoot}>
                  <span class="cli-ws-badge" title={`Dépôt git : ${w.gitRoot}`}>
                    ⎇ git
                  </span>
                </Show>
                <For each={w.markers || []}>
                  {(m) => (
                    <span class="cli-ws-marker" data-context={m === "CLAUDE.md" || m === "AGENTS.md" || undefined}>
                      {m}
                    </span>
                  )}
                </For>
                <button
                  class="btn btn--xs"
                  data-active={props.currentCwd() === w.hostPath || undefined}
                  onClick={() => selectWorkspace(w)}
                  title="Sélectionner comme répertoire de travail courant"
                >
                  {props.currentCwd() === w.hostPath ? "Sélectionné" : "Sélectionner"}
                </button>
              </div>
            )}
          </For>
        </div>
      </Show>

      {/* Contexte hiérarchique CLAUDE.md / AGENTS.md (lecture seule, repliable). */}
      <Show when={context()}>
        {(ctx) => (
          <Collapsible
            variant="default"
            class="cli-ws-context"
            header={<span>📄 Contexte CLAUDE.md / AGENTS.md (lecture seule)</span>}
          >
            <pre class="cli-json cli-ws-context-pre">{ctx().text}</pre>
          </Collapsible>
        )}
      </Show>
    </div>
  );
};
